import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../auth/state';
import feedbackManager from './feedback_manager';
import './feedback_admin.css';
import DashboardFooter from '../../components/DashboardFooter';
import DashboardHeader from '../../components/DashboardHeader';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../auth/firebase';
import cacheManager from '../../auth/cache_manager';

function AdminFeedback() {
  const navigate = useNavigate();
  const { isAdmin, user } = useAuthStore();
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedFeedback, setExpandedFeedback] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }

        // # Dispatch theme change event for footer and other components
        const themeChangeEvent = new CustomEvent('theme_change', {
          detail: { isDark: newTheme }
        });
        window.dispatchEvent(themeChangeEvent);
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Load feedbacks
  useEffect(() => {
    const loadFeedbacks = async () => {
      try {
        const data = await feedbackManager.getAllFeedbacks();
        setFeedbacks(data);
      } catch (error) {
        console.error('Error loading feedbacks:', error);
        setError('Failed to load feedbacks. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadFeedbacks();
  }, []);

  // # Redirect if not admin
  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]);

  // # Handle marking feedback as read
  const handleMarkAsRead = async (feedbackId, yearDocId) => {
    try {
      await feedbackManager.updateReadStatus(feedbackId, yearDocId);
      setFeedbacks(feedbacks.map(feedback => 
        feedback.id === feedbackId 
          ? { ...feedback, metadata: { ...feedback.metadata, read_status: true }}
          : feedback
      ));
    } catch (error) {
      console.error('Error marking feedback as read:', error);
      alert('Failed to mark feedback as read. Please try again.');
    }
  };

  // # Handle deleting feedback
  const handleDelete = async (feedbackId, yearDocId) => {
    if (window.confirm('Are you sure you want to delete this feedback?')) {
      try {
        await feedbackManager.deleteFeedback(feedbackId, yearDocId);
        setFeedbacks(feedbacks.filter(feedback => feedback.id !== feedbackId));
      } catch (error) {
        console.error('Error deleting feedback:', error);
        alert('Failed to delete feedback. Please try again.');
      }
    }
  };

  // # Format date and time
  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    return {
      date: date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
      time: date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      })
    };
  };

  // # Truncate content for preview
  const truncateContent = (content, maxLength = 150) => {
    if (content.length <= maxLength) return content;
    return content.substr(0, maxLength) + '...';
  };

  // # Handle clearing all feedbacks
  const handleClearAll = async () => {
    if (window.confirm('Are you sure you want to delete ALL feedbacks? This action cannot be undone.')) {
      try {
        // Get unique year documents
        const yearDocs = [...new Set(feedbacks.map(f => f.yearDoc))];
        
        // Clear each year document
        for (const yearDoc of yearDocs) {
          await feedbackManager.clearAllFeedbacks(yearDoc);
        }
        
        setFeedbacks([]);
        alert('All feedbacks have been cleared successfully.');
      } catch (error) {
        console.error('Error clearing feedbacks:', error);
        alert('Failed to clear feedbacks. Please try again.');
      }
    }
  };

  return (
    <div className={`admin-feedback-container ${isDarkMode ? 'admin-feedback-dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />

      <main className="admin-feedback-main">
        <div className="admin-feedback-content">
          <div className="feedback-header-section">
            <h2>User Feedbacks</h2>
            {feedbacks.length > 0 && (
              <button
                className="clear-all-button"
                onClick={handleClearAll}
              >
                Clear All Feedbacks
              </button>
            )}
          </div>
          
          {loading && (
            <div className="loading-state">Loading feedbacks...</div>
          )}

          {error && (
            <div className="error-state">{error}</div>
          )}

          {!loading && !error && (
            <div className="feedbacks-grid">
              {feedbacks.length === 0 ? (
                <div className="no-feedbacks">
                  No feedbacks available
                </div>
              ) : (
                feedbacks.map(feedback => {
                  const datetime = formatDateTime(feedback.metadata.createdAt);
                  const isExpanded = expandedFeedback === feedback.id;

                  return (
                    <div 
                      key={feedback.id} 
                      className={`feedback-tile ${feedback.metadata.read_status ? 'read' : 'unread'}`}
                    >
                      <div className="feedback-tile-header">
                        <div className="feedback-meta">
                          <span className="feedback-author">{feedback.metadata.by_name}</span>
                          <span className="feedback-email">({feedback.metadata.by_email})</span>
                          <div className="feedback-datetime">
                            <span className="feedback-date">{datetime.date}</span>
                            <span className="feedback-time">{datetime.time}</span>
                          </div>
                        </div>
                        <div className="feedback-status-badge">
                          {feedback.metadata.read_status ? 'Read' : 'Unread'}
                        </div>
                      </div>

                      <div className="feedback-tile-content">
                        {isExpanded ? feedback.content : truncateContent(feedback.content)}
                      </div>

                      <div className="feedback-tile-actions">
                        <button
                          className="action-button read-more"
                          onClick={() => setExpandedFeedback(isExpanded ? null : feedback.id)}
                        >
                          {isExpanded ? 'Show Less' : 'Read More'}
                        </button>
                        
                        {!feedback.metadata.read_status && (
                          <button
                            className="action-button mark-read"
                            onClick={() => handleMarkAsRead(feedback.feedbackId, feedback.yearDoc)}
                          >
                            Mark as Read
                          </button>
                        )}

                        <button
                          className="action-button delete"
                          onClick={() => handleDelete(feedback.feedbackId, feedback.yearDoc)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </main>

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default AdminFeedback; 